import React, { useEffect, useCallback } from 'react';
import { Stack } from 'react-bootstrap';
import TopStories from 'components/TopStories';
import CreatePost from 'components/CreatePost';
import SocialPost from 'components/SocialPost';
import { useGetPostQuery } from 'services';
import useFeedsStore, {
	setFeeds,
	setFeedsMeta,
	setLastFeedPage,
	setLastScrolling,
} from 'store/feeds';
import { dataLimits } from 'constants/index';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SocialPostSkeletonList } from 'components/Common/Skeleton/SocialPostSkeleton';
import AdvertisementPost from 'components/SocialPost/AdvertisementPost';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import '../styles.scss';

const SocialFeedPosts: React.FC = () => {
	const { translate } = useTranslate();
	const {
		feeds: feedsList = [],
		feedsMeta,
		lastFeedPage,
		lastScrolling,
	}: Record<string, any> = useFeedsStore((state) => state);

	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	const searchString = `order=DESC&page=${lastFeedPage || 1}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const {
		data: { docs: postList = {}, meta = {} } = {},
		status,
		isStale,
		refetch,
	} = useGetPostQuery({
		searchString: searchString,
		userToken: isAuthenticated,
		isFetch: lastFeedPage === feedsMeta?.page ? false : true,
	});

	useEffect(() => {
		if (typeof refetch === 'function') {
			refetch();
		}
	}, [isAuthenticated]);

	const loadMoreFunc = useCallback(() => {
		setLastFeedPage(lastFeedPage + 1);
	}, [lastFeedPage]);

	const handleScroll = useCallback((event: any) => {
		const scrollTop = event.target.scrollTop;
		setLastScrolling(scrollTop);
	}, []);

	useEffect(() => {
		if (
			status === 'success' &&
			postList &&
			Array.isArray(postList.results) &&
			postList.results.length > 0 &&
			isStale
		) {
			setFeeds([...feedsList, ...postList.results]);
			setFeedsMeta(meta);
			setLastFeedPage(meta?.page || 1);
		}
	}, [postList, status]);

	useEffect(() => {
		const scrollableDiv = document.getElementById('infiniteDiv-social-feeds');
		if (scrollableDiv) {
			scrollableDiv.scrollTop = lastScrolling;
		}
	}, [lastScrolling, feedsList.length]);

	const refreshFeeds = useCallback(() => {
		refetch().then((res: any) => {
			if (res?.isSuccess) {
				setFeeds(res?.data?.docs?.results);
				setFeedsMeta(res?.data?.meta);
				setLastFeedPage(res?.data?.meta?.page || 1);
			}
		});
	}, [refetch]);

	return (
		<div className="social-feed">
			<div className="social-feed-left">
				<Stack className="social-feed-posts">
					<TopStories />
					<CreatePost />
					{feedsList?.length > 0 ? (
						<InfiniteScroll
							dataLength={feedsList.length}
							next={loadMoreFunc}
							hasMore={feedsMeta?.hasNextPage}
							loader={feedsList.length >= 1 && <SocialPostSkeletonList count={3} noButton={true} />}
							scrollableTarget={`infiniteDiv-social-feeds right-area`}
							scrollThreshold="300px"
							refreshFunction={loadMoreFunc}
							pullDownToRefreshThreshold={50}
							onScroll={handleScroll}
							pullDownToRefreshContent={
								<h3 style={{ textAlign: 'center' }}>
									&#8595; {translate('Common.pullDownToRefresh')}
								</h3>
							}
							releaseToRefreshContent={
								<h3 style={{ textAlign: 'center' }}>
									&#8593; {translate('Common.releaseToRefresh')}{' '}
								</h3>
							}
						>
							{feedsList.length > 0 ? (
								feedsList.map((post: any, index: number) =>
									post?.isAdvertisement ? (
										<AdvertisementPost
											post={post}
											key={`${post?.postId}-${index}-${post?.userId}`}
										/>
									) : (
										<SocialPost
											post={post}
											key={`${post?.postId}-${index}-${post?.userId}`}
											refetch={refreshFeeds}
										/>
									),
								)
							) : (
								<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
							)}
						</InfiniteScroll>
					) : (
						<>
							{status === 'pending' ? (
								<SocialPostSkeletonList count={3} noButton={true} />
							) : (
								<Stack className="fw-bold">{translate('Common.noPostFound')} </Stack>
							)}
						</>
					)}
				</Stack>
			</div>
		</div>
	);
};

export default SocialFeedPosts;
